import { ChangeEvent, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { Colors, Sizes } from '@/components/ui/types';

import Fieldset from '../fieldset';
import { ToggleProps } from './types';
import useIndeterminate from './useIndeterminate';

const sizeClasses: Record<Sizes, string> = {
  xs: 'toggle-xs',
  sm: 'toggle-sm',
  md: 'toggle-md',
  lg: 'toggle-lg',
  xl: 'toggle-xl',
};

const indeterminateClasses =
  'grid-cols-[0.5fr_1fr_0.5fr] [&>*:nth-child(2)]:opacity-0';

const colorClasses: Record<Colors, string> = {
  default: '',
  neutral: 'toggle-neutral',
  primary: 'toggle-primary',
  secondary: 'toggle-secondary',
  accent: 'toggle-accent',
  info: 'toggle-info',
  success: 'toggle-success',
  warning: 'toggle-warning',
  error: 'toggle-error',
};

const ToggleBase = forwardRef<HTMLInputElement, ToggleProps>(
  (
    {
      className,
      size = 'md',
      color = 'default',
      indeterminate = false,
      checkedIcon,
      uncheckedIcon,
      onChange,
      ...props
    },
    ref,
  ) => {
    const { setInputRef, syncIndeterminate } = useIndeterminate(
      indeterminate,
      ref,
    );

    const hasIcons = !!checkedIcon || !!uncheckedIcon;

    function handleChange(event: ChangeEvent<HTMLInputElement>) {
      syncIndeterminate();
      onChange?.(event);
    }

    const classes = twMerge(
      'toggle',
      sizeClasses[size],
      colorClasses[color],
      hasIcons && props.disabled && 'cursor-not-allowed opacity-30',
      indeterminate && indeterminateClasses,
      className,
    );

    if (hasIcons) {
      return (
        <label className={classes}>
          <input
            ref={setInputRef}
            type="checkbox"
            {...props}
            onChange={handleChange}
          />
          {uncheckedIcon ?? <span />}
          {checkedIcon ?? <span />}
        </label>
      );
    }

    return (
      <input
        ref={setInputRef}
        type="checkbox"
        className={classes}
        {...props}
        onChange={handleChange}
      />
    );
  },
);

ToggleBase.displayName = 'Toggle';

export const Toggle = Object.assign(ToggleBase, {
  Fieldset,
});
